/**
 * Pure agreement scoring for the ski resort compass.
 * Answers and stances use Likert positions 1–4.
 */

const LIKERT_MAX_DISTANCE = 3;
const IMPORTANCE_MULTIPLIER = 2;

/**
 * @typedef {{ question_key: string, value: number|null, important?: boolean }} CompassAnswer
 * @typedef {{ question_key: string, position: number }} CompassStance
 * @typedef {{ resort_key: string, stances?: CompassStance[] }} CompassResort
 * @typedef {{ resortKey: string, percent: number, orderIndex: number }} RankedResort
 */

/**
 * @param {number} userValue
 * @param {number} resortPosition
 * @returns {number} 0–1
 */
export const questionScore = (userValue, resortPosition) => {
  const distance = Math.abs(Number(userValue) - Number(resortPosition));
  if (Number.isNaN(distance)) {
    return 0;
  }
  return 1 - Math.min(Math.max(distance, 0), LIKERT_MAX_DISTANCE) / LIKERT_MAX_DISTANCE;
};

/**
 * @param {CompassAnswer[]} answers
 * @param {CompassResort[]} resorts
 * @param {{ warn?: (msg: string) => void }} [options]
 * @returns {{ ranked: RankedResort[], answeredCount: number }}
 */
export const computeAgreement = (answers, resorts, options = {}) => {
  const warn = options.warn || (() => {});
  const answerByKey = new Map();

  (answers || []).forEach((answer) => {
    if (!answer?.question_key) {
      return;
    }
    answerByKey.set(answer.question_key, answer);
  });

  const answeredCount = Array.from(answerByKey.values()).filter(
    (a) => a.value != null && a.value >= 1 && a.value <= 4,
  ).length;

  const ranked = (resorts || [])
    .map((resort, orderIndex) => {
      const resortKey = resort?.resort_key;
      if (!resortKey) {
        return null;
      }

      const stanceByKey = new Map();
      (resort.stances || []).forEach((stance) => {
        if (stance?.question_key) {
          stanceByKey.set(stance.question_key, stance);
        }
      });

      let weightedSum = 0;
      let weightTotal = 0;

      answerByKey.forEach((answer, questionKey) => {
        const value = answer.value;
        if (value == null || value < 1 || value > 4) {
          return;
        }

        const stance = stanceByKey.get(questionKey);
        const position = Number(stance?.position);
        if (!stance || Number.isNaN(position) || position < 1 || position > 4) {
          warn(
            `Compass resort "${resortKey}" missing stance for question "${questionKey}".`,
          );
          return;
        }

        const weight = answer.important ? IMPORTANCE_MULTIPLIER : 1;
        weightedSum += questionScore(value, position) * weight;
        weightTotal += weight;
      });

      const percent =
        weightTotal > 0 ? Math.round((100 * weightedSum) / weightTotal) : 0;

      return { resortKey, percent, orderIndex };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (b.percent !== a.percent) {
        return b.percent - a.percent;
      }
      return a.orderIndex - b.orderIndex;
    });

  return { ranked, answeredCount };
};

export const LIKERT_OPTIONS = [
  { value: 1, labelKey: 'disagreeStrongly' },
  { value: 2, labelKey: 'disagreeSomewhat' },
  { value: 3, labelKey: 'agreeSomewhat' },
  { value: 4, labelKey: 'agreeStrongly' },
];

export { IMPORTANCE_MULTIPLIER, LIKERT_MAX_DISTANCE };
